import cx from 'classnames';
import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Icon, InformationModal} from 'components';
import {ParticipantAsyncRequest, AsyncRequestStatusName, SumaDocumentVerificationData, AsyncRequestTypeName} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {buildTranslationKey} from 'utils';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.SUMA_DOCUMENT_VERIFICATION_MODAL;
const SDK_T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.SUMA_DOCUMENT_VERIFICATION_SDK_MODAL;

type Props = {
    open: boolean;
    asyncRequest: ParticipantAsyncRequest<SumaDocumentVerificationData>;
    onClose: () => void;
};

export const SumaDocumentVerificationModal: React.FC<Props> = ({open, asyncRequest, onClose}: Props) => {
    const {t} = useTranslation();
    const {success, failed} = useMemo(() => {
        const successStatus = asyncRequest?.status.name === AsyncRequestStatusName.Success;
        return {success: successStatus, failed: !successStatus};
    }, [asyncRequest]);
    const data = useMemo(() => asyncRequest?.data, [asyncRequest]);

    const title = useMemo(() => {
        if (asyncRequest?.type === AsyncRequestTypeName.SumaDocumentVerificationSDK) {
            return t(success ? SDK_T_KEYS.TITLE_SUCCESS : SDK_T_KEYS.TITLE_FAILED);
        }
        return t(success ? T_KEYS.TITLE_SUCCESS : T_KEYS.TITLE_FAILED);
    }, [t, asyncRequest, success]);

    const verificationStatusMessage = useMemo(() => {
        const response = data?.response;
        const globalResult = response?.globalResult;
        const tKey = T_KEYS.GLOBAL_RESULT[buildTranslationKey(globalResult)];
        let label;
        if (tKey) {
            label = t(tKey);
        } else if (data?.message === 'Timeout exceeded') {
            return t(T_KEYS.TIMEOUT_EXCEEDED);
        } else {
            label = t(T_KEYS.UNEXPECTED_ERROR);
        }
        label = `${t(T_KEYS.VERIFICATION_STATUS)}: ${label}`;
        if (response?.globalResultDescription) {
            label += ` (${response.globalResultDescription})`;
        }
        return label;
    }, [data, t]);

    const renderVerificationStatusMessage = useCallback(() => (
        <div>{verificationStatusMessage}</div>
    ), [verificationStatusMessage]);

    const verificationStatus = useMemo(() => {
        const status = data?.response?.status || data?.status;
        const tKey = T_KEYS.STATUS[buildTranslationKey(status)];
        if (tKey) {
            return t(tKey);
        }
    }, [data, t]);

    const renderVerificationStatus = useCallback(() => {
        let label = `${t(T_KEYS.STATUS_LABEL)}: ${data.response?.status}`;
        if (verificationStatus) {
            label += ` (${verificationStatus})`;
        }
        return label;
    }, [data, t, verificationStatus]);

    return (
        <InformationModal
            open={open}
            closeTimeoutMS={0}
            fullWidth
            maxWidth='sm'
            onClose={onClose}
        >
            <div className='tw-flex tw-flex-col tw-items-center'>
                {success ?
                    <Icon name='check-circle' color='success' size='8xl' thickness='sm' className='tw-self-center' /> :
                    <Icon name='x-circle' color='alert' size='8xl' thickness='sm' className='tw-self-center' />}
                <div className='tw-inline-block'>
                    <div className={cx('tw-mt-6 tw-ts-h2', {'tw-text-success': success, 'tw-text-alert': failed})}>
                        {title}
                    </div>
                    {data && verificationStatus &&
                        <div className='tw-my-4 tw-ts-h6'>
                            {renderVerificationStatus()}
                        </div>}
                    {data?.response &&
                        <div className='tw-my-4 tw-ts-h6'>
                            {renderVerificationStatusMessage()}
                        </div>}
                </div>
            </div>
        </InformationModal>
    );
};
